import React, { useEffect, useMemo, useState } from "react";
import { Button } from "../ui/button";
import { X } from "lucide-react";
import axios from "axios";
import { toast } from "react-toastify";

import InputField from "../ui/InputField";
import SelectField from "../ui/SelectField";

const TIPOS_LEGACY = [
  "Preventivo",
  "Correctivo",
  "Calibracion",
  "Guia rapida",
  "Ficha tecnica",
  "Manual de usuario",
  "Manual tecnico",
  "Otro",
];

const ModalSubirDocumento = ({ open, onClose, equipoId, onUploaded }) => {
  const [tipo, setTipo] = useState("");
  const [tipoOtro, setTipoOtro] = useState("");
  const [fechaDocumento, setFechaDocumento] = useState("");
  const [fechaExpedicion, setFechaExpedicion] = useState("");
  const [fechaVencimiento, setFechaVencimiento] = useState("");
  const [tiposDocumento, setTiposDocumento] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [file, setFile] = useState(null);
  const API = process.env.REACT_APP_API_URL;

  const tipoSeleccionado = useMemo(
    () => tiposDocumento.find((t) => t._id === tipo) || null,
    [tipo, tiposDocumento],
  );

  useEffect(() => {
    if (!open) return;

    const loadTipos = async () => {
      try {
        const token = localStorage.getItem("token");
        const { data } = await axios.get(`${API}/tipos-documento`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        setTiposDocumento(data || []);
      } catch (error) {
        setTiposDocumento([]);
      }
    };

    loadTipos();
  }, [API, open]);

  if (!open) return null;

  const usingLegacyTypes = tiposDocumento.length === 0;

  const handleUpload = async () => {
    const tipoFinal = usingLegacyTypes
      ? tipo === "Otro"
        ? tipoOtro.trim()
        : tipo
      : tipo;

    if (!tipoFinal || !file || !fechaDocumento) {
      toast.warning("Completa tipo, fecha y archivo");
      return;
    }

    if (tipoSeleccionado?.requiereFechaExpedicion && !fechaExpedicion) {
      toast.warning("Este tipo requiere fecha de expedición");
      return;
    }

    if (tipoSeleccionado?.requiereFechaVencimiento && !fechaVencimiento) {
      toast.warning("Este tipo requiere fecha de vencimiento");
      return;
    }

    const form = new FormData();
    form.append("tipo", usingLegacyTypes ? tipoFinal : tipoSeleccionado?.nombre);
    form.append("fechaDocumento", fechaDocumento);
    form.append("file", file);

    if (!usingLegacyTypes && tipoSeleccionado?._id) {
      form.append("tipoDocumentoId", tipoSeleccionado._id);
    }

    if (fechaExpedicion) {
      form.append("fechaExpedicion", fechaExpedicion);
    }

    if (fechaVencimiento) {
      form.append("fechaVencimiento", fechaVencimiento);
    }

    try {
      setIsLoading(true);
      const token = localStorage.getItem("token");

      await axios.post(`${API}/equipos/docs/upload/${equipoId}`, form, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      toast.success("Documento registrado");
      onUploaded();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.message || "No se pudo cargar el documento");
    } finally {
      setIsLoading(false);
    }
  };

  const options = usingLegacyTypes
    ? TIPOS_LEGACY.map((t) => ({ _id: t, name: t }))
    : tiposDocumento.map((t) => ({ _id: t._id, name: t.nombre }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-2xl max-h-[90vh] bg-white rounded-2xl shadow-lg overflow-hidden flex flex-col">
        <div className="relative px-6 py-4 border-b">
          <h2 className="text-lg font-bold">Subir documento</h2>
          <button
            className="absolute top-4 right-4 text-gray-500 hover:text-gray-800"
            onClick={onClose}
          >
            <X />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-4">
            {usingLegacyTypes && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                No hay tipos configurados para tu empresa. Se habilitó el listado
                fijo anterior.
              </p>
            )}

            <SelectField
              label="Tipo"
              name="tipo"
              value={tipo}
              options={options}
              onChange={(e) => setTipo(e.target.value)}
            />

            {usingLegacyTypes && tipo === "Otro" && (
              <InputField
                label="Especifica el tipo de documento"
                type="text"
                value={tipoOtro}
                onChange={(e) => setTipoOtro(e.target.value)}
                placeholder="Ej. Certificado de conformidad"
              />
            )}

            <InputField
              label="Fecha del documento"
              type="date"
              value={fechaDocumento}
              onChange={(e) => setFechaDocumento(e.target.value)}
            />

            {(tipoSeleccionado?.requiereFechaExpedicion || usingLegacyTypes) && (
              <InputField
                label={`Fecha de expedición${
                  tipoSeleccionado?.requiereFechaExpedicion ? " *" : ""
                }`}
                type="date"
                value={fechaExpedicion}
                onChange={(e) => setFechaExpedicion(e.target.value)}
              />
            )}

            {(tipoSeleccionado?.requiereFechaVencimiento || usingLegacyTypes) && (
              <InputField
                label={`Fecha de vencimiento${
                  tipoSeleccionado?.requiereFechaVencimiento ? " *" : ""
                }`}
                type="date"
                value={fechaVencimiento}
                onChange={(e) => setFechaVencimiento(e.target.value)}
              />
            )}

            <div>
              <label className="block text-sm font-medium mb-1">Archivo</label>
              <input
                type="file"
                className="w-full border rounded p-2 bg-gray-50"
                onChange={(e) => setFile(e.target.files[0])}
              />
            </div>
          </div>
        </div>

        <div className="p-6 border-t">
          <Button
            className="w-full"
            onClick={handleUpload}
            disabled={isLoading}
          >
            {isLoading ? "Subiendo..." : "Subir"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ModalSubirDocumento;
